import { useState } from "react";
import { api } from "../api";
import { Badge, Btn, Spinner } from "../components/Shared";
import {
  ROLE_COLORS, ROLE_LABELS, ROLE_ORDER,
  grantableAnywhere, grantsOf, groupsForRole, roleNeedsGroup,
} from "../auth/roles";

// One account can hold several roles, in several groups — operator in the
// Bioinformatics Lab and group admin in the Metabolomics Lab at the same time.
// The single Role dropdown on the account form can only express one of those,
// so this is where more than one is managed.
//
// **Including your own.** Adding a role to yourself is allowed; taking one away
// is not. The blanket self-lock that used to cover both was justified by
// lockout, and lockout is a property of removal — see `remove_user_role` in
// backend/portal/routers/admin_users.py, which still refuses.
//
// Roles are ticked rather than chosen one at a time. Putting somebody into four
// facilities used to be four passes through Role → Group → Add role.

const key = (role, groupId) => `${role}:${groupId ?? ""}`;

export default function RoleEditor({ me, user, groups, onChange }) {
  const [busy, setBusy]       = useState(false);
  const [picked, setPicked]   = useState({});    // { role: true }
  const [chosen, setChosen]   = useState({});    // { role: { groupId: true } }
  const [results, setResults] = useState(null);  // one line per attempted grant
  const [error, setError]     = useState(null);  // the remove path, which is one call

  const grants = grantsOf(user);
  const isSelf = me?.id === user?.id;

  const roleOptions = grantableAnywhere(me);
  // What the account already holds, so a pair that cannot be granted twice is
  // shown as held rather than offered and then refused with a 409.
  const held = new Set(grants.map(g => key(g.role, g.group_id)));

  const toggleRole  = r => setPicked(p => ({ ...p, [r]: !p[r] }));
  const toggleGroup = (r, id) => setChosen(c => ({
    ...c, [r]: { ...c[r], [id]: !c[r]?.[id] },
  }));

  // Every (role, group) the form would post, in ROLE_ORDER × the order of the
  // `groups` prop. The order is permission-neutral — can_grant only ever gets
  // more permissive as you gain grants — so it is fixed for the sake of a
  // reproducible audit trail and a test that can assert positionally.
  const pending = ROLE_ORDER.filter(r => roleOptions.includes(r) && picked[r]).flatMap(r => {
    if (!roleNeedsGroup(r)) return held.has(key(r, null)) ? [] : [{ role: r, groupId: null }];
    return groupsForRole(me, r, groups)
      .filter(g => chosen[r]?.[g.id] && !held.has(key(r, g.id)))
      .map(g => ({ role: r, groupId: g.id, groupName: g.name }));
  });

  const label = (role, groupName) => ROLE_LABELS[role] + (groupName ? ` · ${groupName}` : "");

  // Removing is still one grant at a time, and still refused on your own
  // account by the backend — the × is not rendered there at all.
  const remove = async grantId => {
    setBusy(true); setError(null);
    try {
      onChange(await api.delete(`/api/admin/users/${user.id}/roles/${grantId}`));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true); setResults(null);
    const lines = [];
    let latest = null;
    // Sequential, not parallel: each call runs sync_role_cache and commits the
    // same row, and two concurrent inserts race the unique index — which turns
    // a clean 409 into a 500.
    for (const p of pending) {
      try {
        latest = await api.post(`/api/admin/users/${user.id}/roles`,
                                { role: p.role, group_id: p.groupId });
        lines.push({ ...p, outcome: "added" });
      } catch (e) {
        if (e?.authExpired || e?.status === 401) {
          // Every remaining call fails the same way, on top of a sign-out that
          // is already happening. One notice, not N.
          lines.push({ ...p, outcome: "stopped", detail: "Your session ended." });
          break;
        }
        lines.push(e?.status === 409
          ? { ...p, outcome: "already held" }
          : { ...p, outcome: "refused", detail: e?.detail || e?.message });
      }
    }
    setResults(lines);
    // Untick what landed; leave a failure ticked with its reason, so a retry is
    // one click rather than a re-selection.
    const done = new Set(lines.filter(l => l.outcome === "added" || l.outcome === "already held")
                              .map(l => key(l.role, l.groupId)));
    setChosen(c => Object.fromEntries(Object.entries(c).map(([r, gs]) => [
      r, Object.fromEntries(Object.entries(gs).filter(([id, on]) => on && !done.has(key(r, Number(id))))),
    ])));
    setPicked(p => Object.fromEntries(Object.entries(p).filter(([r, on]) =>
      on && !(!roleNeedsGroup(r) && done.has(key(r, null))))));
    setBusy(false);
    if (latest) onChange(latest);
  };

  const legendStyle = { fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em", textTransform: "uppercase", padding: 0 };
  const boxStyle    = { border: 0, margin: 0, padding: 0, minWidth: 0 };
  const rowStyle    = { display: "flex", alignItems: "center", gap: 6, fontSize: 13 };
  const muted       = { color: "#6b7280", fontSize: 12 };

  return (
    <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #e5e7eb" }}>
      <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700 }}>Roles</h4>
      <p style={{ margin: "0 0 12px", color: "#6b7280", fontSize: 13 }}>
        An account can hold several — for example operator in one facility and group admin
        in another. A plain user needs no group at all: they reach a project because they
        own it or were assigned to it.
      </p>

      {error && (
        <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        {grants.map(g => (
          <span key={g.id ?? key(g.role, g.group_id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Badge color={ROLE_COLORS[g.role] || "gray"}>
              {label(g.role, g.group_name)}
            </Badge>
            {g.id != null && grants.length > 1 && !isSelf && (
              <button onClick={() => remove(g.id)} disabled={busy}
                      aria-label={`Remove ${ROLE_LABELS[g.role] || g.role}${g.group_name ? ` in ${g.group_name}` : ""}`}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 15, lineHeight: 1, padding: "0 2px" }}>×</button>
            )}
          </span>
        ))}
      </div>

      {isSelf && (
        // Adding is open; removing is not, and without this the missing × reads
        // as a rendering fault rather than as the rule it is.
        <p style={{ margin: "0 0 14px", ...muted }}>
          You cannot remove your own roles. Ask another administrator.
        </p>
      )}

      {roleOptions.length === 0 ? (
        <p style={{ margin: "8px 0 0", color: "#6b7280", fontSize: 13 }}>You cannot grant any role.</p>
      ) : (
        <>
          {/* Not <Field>: it wires its label onto the first control it finds,
              which here is the first checkbox — the group would end up named
              after one of its members. fieldset/legend names the set. */}
          <fieldset style={boxStyle}>
            <legend style={legendStyle}>Roles</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "6px 0 14px" }}>
              {ROLE_ORDER.filter(r => roleOptions.includes(r)).map(r => {
                const scoped    = roleNeedsGroup(r);
                const heldFlat  = !scoped && held.has(key(r, null));
                const available = scoped ? groupsForRole(me, r, groups) : [];
                return (
                  <div key={r}>
                    <label style={rowStyle}>
                      <input type="checkbox" aria-label={ROLE_LABELS[r]}
                             checked={heldFlat || !!picked[r]} disabled={busy || heldFlat}
                             onChange={() => toggleRole(r)} />
                      {ROLE_LABELS[r]}
                      {heldFlat && <span style={muted}>· already held</span>}
                    </label>

                    {scoped && picked[r] && (
                      <fieldset style={{ ...boxStyle, margin: "6px 0 0 24px" }}>
                        {/* Its own named group: without one, "BINF" appears
                            once per ticked role and names nothing in
                            particular. */}
                        <legend style={{ ...legendStyle, fontSize: 11 }}>
                          Groups for {ROLE_LABELS[r]}
                        </legend>
                        {available.length === 0 ? (
                          <p style={{ margin: "4px 0 0", ...muted }}>
                            There is no group in which you may grant {ROLE_LABELS[r]}.
                          </p>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 4 }}>
                            {available.map(g => {
                              const already = held.has(key(r, g.id));
                              return (
                                <label key={g.id} style={rowStyle}>
                                  <input type="checkbox" aria-label={g.name}
                                         checked={already || !!chosen[r]?.[g.id]}
                                         disabled={busy || already}
                                         onChange={() => toggleGroup(r, g.id)} />
                                  {g.name}
                                  {already && <span style={muted}>· already held</span>}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </fieldset>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>

          <Btn onClick={submit} disabled={busy || pending.length === 0}>
            {busy ? <><Spinner /> Working…</>
                  : pending.length ? `Add ${pending.length} role${pending.length === 1 ? "" : "s"}`
                  : "Add roles"}
          </Btn>
        </>
      )}

      {results && (
        // One line per attempted grant. A single "something went wrong" over a
        // loop makes a half-applied change look identical to a failed one.
        <div role="status" style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
          {results.map(l => (
            <div key={key(l.role, l.groupId)} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600 }}>{label(l.role, l.groupName)}</span>
              <span style={{ color: l.outcome === "added" ? "#047857" : l.outcome === "already held" ? "#6b7280" : "#b91c1c" }}>
                {l.outcome}
              </span>
              {l.detail && <span style={muted}>— {l.detail}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
